import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Button } from "../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { PersonPicker } from "./PersonPicker";

export const MGR_POSITIONS = ["Business Contact (BC)", "Lead Driver", "Operations Manager", "Owner / Authorized Officer"];
export const HR_POSITIONS = ["HR Representative"];

export const inp = "bg-slate-950 border-slate-700 text-white h-10";
export const card = "rounded-2xl border border-slate-800 bg-slate-900/60 p-4 sm:p-5";
export const lbl = "text-[10px] uppercase tracking-widest text-slate-500 block mb-1";

export function nowHM() {
  return new Date().toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", timeZone: "America/New_York" });
}

export function Field({ label, children, hint, className = "" }) {
  return <div className={className}><label className={lbl}>{label}</label>{children}{hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}</div>;
}

export function Sel({ value, onChange, options, placeholder = "Select…", testid, disabled }) {
  const opts = options.map((o) => (Array.isArray(o) ? o : [o, o]));
  return (
    <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testid} className={inp}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-slate-900 border-slate-700 text-white max-h-72">{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function YesNo({ value, onChange, testid, disabled }) {
  return (
    <div className="flex gap-1" data-testid={testid}>
      {[[true, "Yes"], [false, "No"]].map(([v, l]) => <button key={l} type="button" disabled={disabled} onClick={() => onChange(v)} className={`px-3 h-10 rounded-lg text-sm border ${value === v ? "bg-amber-500 text-slate-950 border-amber-500 font-bold" : "border-slate-700 text-slate-300"}`}>{l}</button>)}
    </div>
  );
}

export function Choice({ value, onChange, options, testid, disabled }) {
  return (
    <div className="grid sm:grid-cols-3 gap-2" data-testid={testid}>
      {options.map(([v, l, tone]) => <button key={v} type="button" disabled={disabled} onClick={() => onChange(v)} className={`text-left px-3 py-2.5 rounded-xl text-sm border transition-colors ${value === v ? `${tone || "bg-amber-500 border-amber-500 text-slate-950"} font-bold` : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>{l}</button>)}
    </div>
  );
}

export function TimeNow({ value, onChange, testid, disabled }) {
  return (
    <div className="flex gap-2">
      <Input data-testid={testid} type="time" value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inp} font-mono w-[140px]`} disabled={disabled} />
      {!disabled && <Button type="button" variant="outline" onClick={() => onChange(nowHM())} className="h-10 border-slate-700 text-amber-300 text-xs">Now</Button>}
    </div>
  );
}

/* Local form state for one section; flush() saves only changed keys. */
export function useSectionForm(data, save) {
  const [form, setForm] = useState(data || {});
  useEffect(() => { setForm(data || {}); }, [data]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const flush = (extra = {}) => {
    const merged = { ...form, ...extra };
    const diff = Object.fromEntries(Object.entries(merged).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify((data || {})[k])));
    if (Object.keys(diff).length) save(diff);
  };
  const setNow = (k, v) => { set(k, v); flush({ [k]: v }); };
  return { form, set, flush, setNow };
}

/* Generic item dialog driven by a field spec */
export function ItemDialog({ open, onClose, title, fields, initial, onSave, testid }) {
  const [f, setF] = useState(initial || {});
  useEffect(() => { setF(initial || {}); }, [initial, open]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={testid} className="bg-slate-900 border-slate-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="display uppercase">{title}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {fields.filter((fd) => !fd.showIf || fd.showIf(f)).map((fd) => (
            <Field key={fd.key} label={fd.label} className={fd.wide ? "sm:col-span-2" : ""}>
              {fd.type === "person" && <PersonPicker testid={`item-${fd.key}`} value={f[fd.key]} company={fd.company} positions={fd.positions} multiple={fd.multiple} allowFreeText={fd.allowFreeText} placeholder={fd.placeholder} onChange={(v, p) => { set(fd.key, v); fd.onPick && fd.onPick(p, set); }} />}
              {fd.type === "datetime" && <Input data-testid={`item-${fd.key}`} type="datetime-local" value={f[fd.key] || ""} onChange={(e) => set(fd.key, e.target.value)} className={`${inp} font-mono`} />}
              {fd.type === "select" && <Sel value={f[fd.key]} onChange={(v) => set(fd.key, v)} options={fd.options} testid={`item-${fd.key}`} />}
              {fd.type === "bool" && <YesNo value={f[fd.key]} onChange={(v) => set(fd.key, v)} testid={`item-${fd.key}`} />}
              {fd.type === "time" && <TimeNow value={f[fd.key]} onChange={(v) => set(fd.key, v)} testid={`item-${fd.key}`} />}
              {fd.type === "textarea" && <Textarea data-testid={`item-${fd.key}`} value={f[fd.key] || ""} onChange={(e) => set(fd.key, e.target.value)} className="bg-slate-950 border-slate-700 text-white min-h-[70px]" />}
              {fd.type === "datalist" && <><Input data-testid={`item-${fd.key}`} list={`dl-${fd.key}`} value={f[fd.key] || ""} onChange={(e) => { set(fd.key, e.target.value); fd.onPick && fd.onPick(e.target.value, set); }} className={inp} placeholder={fd.placeholder} /><datalist id={`dl-${fd.key}`}>{fd.options.map((o) => <option key={o} value={o} />)}</datalist></>}
              {(!fd.type || ["text", "number", "date"].includes(fd.type)) && <Input data-testid={`item-${fd.key}`} type={fd.type || "text"} value={f[fd.key] ?? ""} onChange={(e) => set(fd.key, fd.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)} className={inp} placeholder={fd.placeholder} />}
            </Field>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-2"><Button variant="outline" onClick={onClose} className="border-slate-700 text-slate-300">Cancel</Button><Button data-testid="item-save" onClick={() => onSave(f)} className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold">Save</Button></div>
      </DialogContent>
    </Dialog>
  );
}

/* List of items with add/edit/remove */
export function ItemList({ title, items, fields, columns, onAdd, onUpdate, onRemove, canEdit, canRemove, addLabel = "Add", testid, empty = "None recorded" }) {
  const [dlg, setDlg] = useState(null); // null | {item}
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-800 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-slate-950/60"><div className="text-xs uppercase tracking-widest text-slate-400">{title} <span className="text-amber-300">({items.length})</span></div>{canEdit && <Button size="sm" data-testid={`${testid}-add`} onClick={() => setDlg({})} className="h-8 bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-xs"><Plus className="w-3.5 h-3.5 mr-1" />{addLabel}</Button>}</div>
      {items.length === 0 ? <div className="p-4 text-sm text-slate-500">{empty}</div> : (
        <ul className="divide-y divide-slate-800">
          {items.map((it) => (
            <li key={it.id} data-testid={`${testid}-row`} className="p-3 flex gap-3 items-start text-sm">
              <div className="flex-1 min-w-0 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-1">
                {columns.map(([k, l, fmt]) => (it[k] != null && it[k] !== "" && !(Array.isArray(it[k]) && !it[k].length)) && <div key={k} className="min-w-0"><span className="text-[10px] uppercase tracking-widest text-slate-500">{l}: </span><span className="text-slate-200 break-words">{fmt ? fmt(it[k]) : Array.isArray(it[k]) ? it[k].join(", ") : String(it[k])}</span></div>)}
                {it.duplicate_warning && <div className="lg:col-span-4 text-[11px] text-amber-300">{it.duplicate_warning}</div>}
                <div className="lg:col-span-4 text-[10px] text-slate-600">Added by {it.added_by}{it.updated_by ? ` · edited by ${it.updated_by}` : ""}</div>
              </div>
              {canEdit && <div className="flex gap-1 shrink-0"><button data-testid={`${testid}-edit`} onClick={() => setDlg({ item: it })} className="p-1.5 rounded hover:bg-slate-800 text-slate-400"><Pencil className="w-4 h-4" /></button>{canRemove && <button data-testid={`${testid}-remove`} onClick={() => onRemove(it)} className="p-1.5 rounded hover:bg-slate-800 text-rose-400"><Trash2 className="w-4 h-4" /></button>}</div>}
            </li>
          ))}
        </ul>
      )}
      {dlg && <ItemDialog open title={dlg.item ? `Edit — ${title}` : `${addLabel} — ${title}`} fields={fields} initial={dlg.item || {}} testid={`${testid}-dialog`} onClose={() => setDlg(null)} onSave={async (f) => { dlg.item ? await onUpdate(dlg.item, f) : await onAdd(f); setDlg(null); }} />}
    </div>
  );
}

export const STATUS_OPTS = ["Open", "In progress", "Resolved"];
export const SEVERITY_OPTS = ["Low", "Medium", "High", "Unsafe — do not dispatch"];
